import React from 'react';
import Section from './Section';
import { LABEL_FEATURES_LIST, SHIPPING_CANADA, SHIPPING_USA, RETURN_POLICY_ADDRESS } from '../constants';
import LabelIcon from './icons/LabelIcon';
import CurrencyDollarIcon from './icons/CurrencyDollarIcon';
import TruckIcon from './icons/TruckIcon';

const ShippingInfoCard: React.FC<{ title: string; country: string; details: typeof SHIPPING_CANADA | typeof SHIPPING_USA }> = ({ title, country, details }) => (
  <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300 h-full flex flex-col">
    <div className="flex items-center mb-4">
      <TruckIcon className="w-10 h-10 text-emerald-500 mr-4" />
      <h4 className="text-xl font-semibold text-emerald-700">{title}</h4>
    </div>
    <div className="text-gray-600 space-y-1 mb-3">
      <p><strong className="font-medium">Destination:</strong> {country}</p>
      <p><strong className="font-medium">Estimated Transit:</strong> {details.transitTime}</p>
      <p><strong className="font-medium">Flat Rate Shipping:</strong> ${details.singleRollCost.toFixed(2)}</p>
    </div>
    <p className="text-emerald-600 font-semibold mt-auto text-lg">Orders of {details.freeShippingThreshold} or more rolls ship FREE!</p>
  </div>
);

const OurLabelsSection: React.FC = () => {
  return (
    <Section id="our-labels" title="Our Labels: Quality You Can Trust" className="bg-gray-100">
      <div className="space-y-16">
        {/* Label Features */}
        <div>
          <img 
              src="/purchased-on-premises-labels-with-cans.png" 
              alt="Photo of Purchased On Premises Labels" 
              className="mx-auto mb-6 rounded-lg max-w-80 h-auto" 
            />
          <div className="text-center mb-10">
            <p className="text-2xl font-semibold text-emerald-700">
              Affordable. Durable. Visible.
            </p>
            <p className="mt-3 text-lg text-gray-600 max-w-3xl mx-auto">
              Our 'Purchased on Premises' labels are engineered specifically for the demanding environment of golf courses and beverage service. They perform flawlessly on cans and bottles of adult beverages like beer, ciders, and coolers, enduring the rigors of beer carts and golf bags without breaking the bank.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-10">
            {LABEL_FEATURES_LIST.map((feature, index) => (
              <div key={index} className="flex items-start">
                <div className="flex-shrink-0">
                  <LabelIcon className="w-7 h-7 text-emerald-500" />
                </div>
                <div className="ml-4">
                  <p className="text-lg text-gray-700">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing & Ordering */}
        <div className="pt-12 border-t border-gray-200">
          <h3 className="text-3xl font-semibold text-emerald-700 mb-8 text-center">Colors & Options</h3>
          <div className="max-w-2xl mx-auto text-center bg-white p-8 md:p-10 rounded-xl shadow-xl border border-gray-200">
            <img 
              src="/green-roll.png" 
              alt="Example of a purchased on premises label roll" 
              className="mx-auto mb-6 rounded-lg max-h-48 h-auto" 
            />
            <p className="text-xl text-gray-700 mb-4">
              Each roll contains <strong className="text-emerald-600">1,000 high-quality labels</strong> and is available in 
              <strong><span className="text-orange-500"> orange</span></strong>, 
              <strong><span className="text-green-600"> green</span></strong>, 
              <strong><span className="text-pink-500"> pink</span></strong>, or 
              <strong><span className="text-blue-600"> blue</span></strong>.
            </p>
            <p className="text-xl text-gray-700 mb-4">
              The dimensions of each label are <strong>9.375" wide x 0.75" tall</strong>.
            </p>
            <p className="text-xl text-gray-700 mb-8">
              The minimum purchase is only <strong className="text-emerald-600">one roll</strong>, making it easy to get started and see the benefits for yourself.
            </p>
            <a
              href="#contact"
              className="inline-block px-6 py-2.5 sm:px-8 sm:py-3 text-base sm:text-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-lg"
            >
              Get a Quote or Place an Order
            </a>
          </div>
        </div>
        
        {/* Custom Designs */}
        <div className="pt-12 border-t border-gray-200">
            <h3 className="text-3xl font-semibold text-emerald-700 mb-8 text-center">Custom Label Designs</h3>
          <div className="max-w-2xl mx-auto text-center bg-white p-8 md:p-10 rounded-xl shadow-xl border border-gray-200">
            <p className="text-xl text-gray-700 mb-4">
              Want to feature your golf course logo or a unique design on your labels? We can help!
            </p>
            <p className="text-lg text-gray-600 mb-8">
              Our team can work with you to create custom 'Purchased on Premises' labels that perfectly match your branding. Please note that costs for custom designs are slightly higher, and minimum purchase quantities will apply.
            </p>
            <a
              href="#contact"
              className="inline-block px-6 py-2.5 sm:px-8 sm:py-3 text-base sm:text-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg transition duration-300 ease-in-out transform hover:scale-105 shadow-lg"
            >
              Discuss Your Custom Needs
            </a>
          </div>
        </div>

        {/* Shipping & Returns */}
        <div className="pt-12 border-t border-gray-200">
          <h3 className="text-3xl font-semibold text-emerald-700 mb-10 text-center">Shipping & Returns</h3>
          <div className="space-y-12">
            <div>
              <h4 className="text-2xl font-semibold text-gray-800 mb-6 text-center md:text-left">Shipping Information</h4>
              <p className="text-center md:text-left text-lg text-gray-600 mb-8 max-w-2xl mx-auto md:mx-0">
                We ship your orders promptly to anywhere in the US or Canada. Here are the details:
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <ShippingInfoCard title="Shipping to Canada" country="Canada" details={SHIPPING_CANADA} />
                <ShippingInfoCard title="Shipping to the USA" country="United States" details={SHIPPING_USA} />
              </div>
            </div>

            <div>
              <h4 className="text-2xl font-semibold text-gray-800 mb-6 text-center md:text-left">Our Return Policy</h4>
              <div className="max-w-2xl mx-auto md:mx-0 bg-white p-8 rounded-xl shadow-xl border border-gray-200">
                <h5 className="text-xl font-semibold text-emerald-700 mb-4">Your Satisfaction Guaranteed</h5>
                <p className="text-lg text-gray-600 mb-4">
                  We stand behind the quality of our labels. If you are not satisfied with your purchase for any reason, simply return any unused rolls to us.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  You will receive a <strong className="text-emerald-600">full refund of the purchase price</strong> for each returned roll.
                </p>
                <div className="text-left bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h6 className="text-lg font-medium text-emerald-700 mb-2">Return Address:</h6>
                  <address className="not-italic text-gray-700 text-sm">
                    <strong>{RETURN_POLICY_ADDRESS.name}</strong><br />
                    {RETURN_POLICY_ADDRESS.street}<br />
                    {RETURN_POLICY_ADDRESS.city}, {RETURN_POLICY_ADDRESS.country}
                  </address>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default OurLabelsSection;